const {
  services: servicesConfig,
  observability: observabilityConfig,
  muiProLicenseKey: muiProLicenseKeyConfig,
  apiBaseUrl: apiBaseUrlConfig,
  apiInternalBaseUrl,
  apiWhitelabelConfigUrl,
  locationsList: locationsListConfig,
  grafanaSupportToolboxUrl: grafanaSupportToolboxUrlConfig,
  ...config
} = globalThis.FlexPortalConfig ?? {}

// Parses a tri-state boolean flag. Build-time variables always arrive as strings, so a bare
// `?? false` would treat the string 'false' as truthy. Returning `undefined` when the flag is not
// configured lets callers apply their own default instead of reading "unset" as "disabled".
function parseOptionalBoolean(value: string | boolean | undefined): boolean | undefined {
  if (value === undefined || value === '') return undefined
  if (typeof value === 'boolean') return value

  return value === 'true'
}

const API_BASE_URL = process.env.VITE_APP_API_BASE_URL ?? apiBaseUrlConfig ?? 'https://api.staging.sympower.net'
const API_INTERNAL_BASE_URL =
  process.env.VITE_APP_API_INTERNAL_BASE_URL ?? apiInternalBaseUrl ?? 'https://api.staging-service.sympower.net'
const API_WHITELABEL_CONFIG_BASE_URL =
  process.env.VITE_APP_API_WHITELABEL_CONFIG_URL ??
  apiWhitelabelConfigUrl ??
  'https://whitelabel.staging-service.sympower.net'

// Environment contains all the configuration that is needed for the application to run. All configuration properties can be
// overridden on developmentby environment variables that start with VITE_APP_ (there are some xamples on the .env.example file).
//
// Whare are providing a mechanims to override the configuration properties by using the globalThis.FlexPortalConfig object. This object
// can be set at runtime. This is useful for example when we want to override the configuration properties on some test environments or
// at deployment phase.
//
// The environment object includes some default values, so developers can run the application without setting any environment variable.
const environment = {
  allCountries: (process.env.VITE_APP_ALL_COUNTRIES as CountryIdentifier[] | undefined) ?? [
    'fi',
    'gr',
    'nl',
    'no',
    'se',
  ],

  allLocations: (process.env.VITE_APP_ALL_LOCATIONS as CountryIdentifier[] | undefined) ?? [
    'fi',
    'gr',
    'nl',
    'no',
    'se',
  ],

  defaultLocation: process.env.VITE_APP_DEFAULT_LOCATION ?? 'fi',

  getLocationsList(): LocationInfo[] {
    return (
      locationsListConfig ??
      this.allLocations.map((location) => ({
        location: location,
        country: location,
        price_area: location,
      }))
    )
  },

  apiBaseUrl: API_BASE_URL,

  apiInternalBaseUrl: API_INTERNAL_BASE_URL,

  environmentName: process.env.VITE_APP_ENVIRONMENT_NAME ?? 'development',

  cognitoUserPoolId: process.env.VITE_APP_COGNITO_USER_POOL_ID ?? 'eu-central-1_FhgP6VCj2',

  cognitoWebClientId: process.env.VITE_APP_COGNITO_WEB_CLIENT_ID ?? '1c20k20jsjvbpfm4qcpuj6ru3k',

  // Intentional ||: workflows that skip `version` export an empty string, which ?? would treat as a real version.
  deployedVersion: process.env.VITE_APP_DEPLOY_VERSION || 'N/A in development',

  /**
   * Links to metrics, logs and other observability services.
   *
   * Destructured out of the runtime config above so that the `...config` spread cannot replace
   * this object wholesale: deployed `config.js` files set only some of these keys, and a whole-
   * object override would leave the rest `undefined`.
   */
  observability: {
    grafanaBaseUrl: process.env.VITE_APP_GRAFANA_BASE_URL ?? 'https://grafana.sympower.net',
    temporalUiBaseUrl: process.env.VITE_APP_TEMPORAL_UI_BASE_URL ?? 'https://cloud.temporal.io',
    ...observabilityConfig,
  },

  /** Allows links to legacy UI apps (Admin UI, Electrician UI) */
  legacyUiBaseDomain: process.env.VITE_APP_APP_BASE_URL ?? 'staging-app.sympower.net',

  theme: process.env.VITE_APP_THEME ?? 'sympower',

  useDefaultWhitelabelConfig: process.env.VITE_APP_USE_DEFAULT_WHITELABEL_CONFIG ?? false,

  isDevelopmentMode: process.env.NODE_ENV === 'development',

  getMuiProLicenseKey(): string {
    return (
      process.env.VITE_APP_MUI_PRO_PLAN_LICENSE_KEY ??
      muiProLicenseKeyConfig ??
      'fbb6aac0b4d47c3aaa30432a560b225dTz0xMzM3NjMsRT0xODI0NDIyMzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y'
    )
  },

  googleAnalyticsTrackId: process.env.VITE_APP_GOOGLE_ANALYTICS_TRACK_ID ?? 'G-N0BS5WCB1M',

  googleMapsApiKey: process.env.VITE_APP_GOOGLE_MAPS_API_KEY ?? 'AIzaSyBNINNL0r-WNif0e2P8oOxADOhmQM297Iw',

  coralogixRumPublicKey: process.env.VITE_APP_CORALOGIX_RUM_PUBLIC_KEY ?? '',

  /**
   * Explicit opt-in/opt-out for Coralogix RUM reporting, independent of the environment name.
   * Leave unset to decide automatically: enabled for deployed builds, disabled when running
   * locally. See `shouldInitializeCoralogixRum`.
   */
  coralogixRumEnabled: parseOptionalBoolean(process.env.VITE_APP_CORALOGIX_RUM_ENABLED),

  ...config,

  // List of API micro services that we use to get and manipulate data from Flex Portal.
  // There is more information about the API tree in the following link:
  // https://app.notion.com/p/sympower/3d9b5dfacb3c490d87762ad547e811dd
  services: {
    activationManagerApiUrl:
      process.env.VITE_APP_ACTIVATION_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/activation-manager/activations`,

    deviceManagerApiUrl: process.env.VITE_APP_DEVICE_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/device-management`,

    usersManagerApiUrl: process.env.VITE_APP_USERS_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/users`,

    portfolioManagerApiUrl:
      process.env.VITE_APP_PORTFOLIO_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/portfolio-manager`,

    prequalificationManagerApiUrl:
      process.env.VITE_APP_PREQUALIFICATION_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/prequalification-manager`,

    resourcesManagerApiUrl: process.env.VITE_APP_RESOURCES_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/resource-bff`,
    resourceSelectionManagerApiUrl:
      process.env.VITE_APP_RESOURCE_SELECTION_MANAGER_API_URL ?? `${API_BASE_URL}/api/v2/nordics-static-fcrd`,

    insightsManagerApiUrl: process.env.VITE_APP_INSIGHTS_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/insights`,

    partnersManagerApiUrl: process.env.VITE_APP_PARTNERS_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/partners`,

    sitesManagerApiUrl: process.env.VITE_APP_SITES_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/sites`,

    whitelabelConfigManagerApiUrl:
      process.env.VITE_APP_WHITELABEL_CONFIG_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/whitelabel`,

    whitelabelConfigManagerPublicApiUrl:
      process.env.VITE_APP_WHITELABEL_CONFIG_MANAGER_PUBLIC_API_URL ??
      `${API_WHITELABEL_CONFIG_BASE_URL}/public/api/v1/whitelabel`,

    monolithGatewayManagerApiUrl:
      process.env.VITE_APP_MONOLITH_GATEWAY_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/monolith-api-gw`,

    featureTogglesManagerApiUrl:
      process.env.VITE_APP_FEATURE_TOGGLES_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/features`,

    integrationsManagerApiUrl:
      process.env.VITE_APP_INTEGRATIONS_MANAGER_API_URL ?? `${API_BASE_URL}/api/v1/integrations`,

    databricksSharingApiUrl:
      process.env.VITE_APP_DATABRICKS_SHARING_API_URL ?? `${API_BASE_URL}/api/v1/databricks-sharing`,

    databricksMarketForecasts:
      process.env.VITE_APP_DATABRICKS_MARKET_FORECASTS_API_URL ?? `${API_BASE_URL}/api/v1/market-forecasts`,
    biddingInsightsApiUrl: process.env.VITE_APP_BIDDING_INSIGHTS_API_URL ?? `${API_BASE_URL}/api/v1/bidding-insights`,

    resourceScheduleActionsApiUrl:
      process.env.VITE_APP_RESOURCE_SCHEDULE_ACTIONS_API_URL ?? `${API_BASE_URL}/api/v1/resource/schedule/actions`,

    nordpoolTradeAdapterApiUrl:
      process.env.VITE_APP_NORDPOOL_TRADE_ADAPTER_API_URL ?? `${API_BASE_URL}/api/v1/wholesale/nordpool-trade-adapter`,

    flexBalanceApiUrl: process.env.VITE_FLEXBALANCE_API_URL ?? `${API_BASE_URL}/api/v1/flexbalance`,

    productionPlanApiUrl: process.env.VITE_APP_PRODUCTION_PLAN_API_URL ?? `${API_BASE_URL}/api/v1/production-plans`,

    ...servicesConfig,
  },
  dashboards: {
    grafanaSupportToolboxUrl:
      process.env.VITE_APP_GRAFANA_SUPPORT_TOOLBOX_URL ??
      grafanaSupportToolboxUrlConfig ??
      `https://grafana.sympower.net/d/ee3thy3tqhqtcc/resource-insight?orgId=1&from=now-1h&to=now&timezone=utc&var-environment=staging&var-resourceId=~resource-placeholder~`,
  },
}

export default environment
